import React, { useState, useEffect, useRef, Fragment } from "react";
import { DataElement, ElementVersion, ElementVersionHistory, SourceFieldMappingView } from "@/entities/all";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";
import {
  ArrowLeft,
  Crown,
  Edit,
  Star,
  Key,
  Link2,
  Trash2,
  Pencil,
  ChevronDown,
  ChevronRight,
  History,
  User,
  PlusCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";

import ElementVersionModal from "../components/elements/ElementVersionModal";
import ElementEditModal from "../components/elements/ElementEditModal";
import DeleteDataElementDialog from "../components/elements/DeleteDataElementDialog";

const STATUS_CONFIG = {
  DRAFT:          { bg: 'var(--pill-blue-bg)',    color: 'var(--pill-blue-color)',    label: 'Draft' },
  APPROVED:       { bg: 'var(--pill-green-bg)',   color: 'var(--pill-green-color)',   label: 'Approved' },
  PENDING_REVIEW: { bg: 'var(--pill-amber-bg)',   color: 'var(--pill-amber-color)',   label: 'Pending Review' },
  DEPRECATED:     { bg: 'var(--pill-red-bg)',     color: 'var(--pill-red-color)',     label: 'Deprecated' },
};

function getDataTypePill(type) {
  const t = (type || '').toLowerCase();
  if (t === 'string' || t === 'large_string')
    return { bg: 'var(--pill-neutral-bg)',  color: 'var(--pill-neutral-color)',  label: 'String' };
  if (t.startsWith('int') || t.startsWith('uint'))
    return { bg: 'var(--pill-int-bg)',      color: 'var(--pill-int-color)',      label: type };
  if (t.startsWith('float') || t === 'decimal128')
    return { bg: 'var(--pill-float-bg)',    color: 'var(--pill-float-color)',    label: type };
  if (t === 'bool')
    return { bg: 'var(--pill-green-bg)',    color: 'var(--pill-green-color)',    label: 'Boolean' };
  if (t.includes('date') || t.includes('time') || t === 'timestamp' || t === 'duration')
    return { bg: 'var(--pill-date-bg)',     color: 'var(--pill-date-color)',     label: type };
  if (t === 'uuid')
    return { bg: 'var(--pill-uuid-bg)',     color: 'var(--pill-uuid-color)',     label: 'UUID' };
  if (t === 'binary' || t === 'large_binary')
    return { bg: 'var(--pill-binary-bg)',   color: 'var(--pill-binary-color)',   label: 'Binary' };
  return { bg: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-color)', label: type || '—' };
}

function Pill({ bg, color, label }) {
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2" style={{ backgroundColor: bg }}>
      <span className="size-[10px] rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color }}>{label}</span>
    </span>
  );
}

export default function ElementDetails() {
  const navigate = useNavigate();
  const [element, setElement] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [filteredVersions, setFilteredVersions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [versionHistory, setVersionHistory] = useState({});
  const [historyPage, setHistoryPage] = useState({});

  const params = new URLSearchParams(window.location.search);
  const elementId = params.get('id') ? Number(params.get('id')) : undefined;
  const tabParam = params.get('tab');

  useEffect(() => {
    if (elementId) loadElementDetails();
  }, [elementId]);

  const tabAutoOpened = useRef(false);
  useEffect(() => {
    if (tabParam && element && versions.length > 0 && !tabAutoOpened.current) {
      const current = versions.find(v => v.version_number === element.current_version_number) ||
                     versions.find(v => v.status === 'APPROVED') ||
                     versions[0];
      if (current) {
        tabAutoOpened.current = true;
        setSelectedVersion(current);
        setIsVersionModalOpen(true);
      }
    }
  }, [tabParam, element, versions]);

  useEffect(() => {
    filterVersions();
  }, [versions, statusFilter]);

  const loadElementDetails = async () => {
    setIsLoading(true);
    try {
      const [elementData, versionsData, mappingsData] = await Promise.all([
        DataElement.list().then(elements => elements.find(e => e.id === elementId)),
        ElementVersion.filter({ element_id: elementId }, '-version_number'),
        SourceFieldMappingView.filter({ canonical_element_id: elementId }, 'source_name'),
      ]);
      setElement(elementData);
      setVersions(versionsData);
      setMappings(mappingsData);
      const current = versionsData.find(v => v.version_number === elementData?.current_version_number) ||
                     versionsData.find(v => v.status === 'APPROVED') ||
                     versionsData[0];
      setCurrentVersion(current);
    } catch (error) {
      console.error('Error loading element details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterVersions = () => {
    setFilteredVersions(statusFilter === 'all' ? versions : versions.filter(v => v.status === statusFilter));
  };

  const handleSetAsCurrent = async (version) => {
    try {
      await DataElement.update(elementId, { current_version_number: version.version_number });
      setCurrentVersion(version);
      loadElementDetails();
    } catch (error) {
      console.error('Error setting current version:', error);
    }
  };

  const refreshHistoryForExpanded = async () => {
    const expandedIds = Object.keys(expandedVersions).filter(id => expandedVersions[id]);
    for (const versionId of expandedIds) {
      await loadHistoryPage(parseInt(versionId), historyPage[versionId]?.page || 0);
    }
  };

  const handleVersionSaved = () => { refreshHistoryForExpanded(); };

  const refreshElementDetails = async () => {
    try {
      const [elementData, versionsData] = await Promise.all([
        DataElement.list().then(elements => elements.find(e => e.id === elementId)),
        ElementVersion.filter({ element_id: elementId }, '-version_number'),
      ]);
      setElement(elementData);
      setVersions(versionsData);
      const current = versionsData.find(v => v.version_number === elementData?.current_version_number) ||
                     versionsData.find(v => v.status === 'APPROVED') ||
                     versionsData[0];
      setCurrentVersion(current);
    } catch (error) {
      console.error('Error refreshing element details:', error);
    }
  };

  const handleVersionModalClose = () => {
    setIsVersionModalOpen(false);
    setSelectedVersion(null);
    refreshElementDetails();
  };

  const handleEditVersion = (version) => { setSelectedVersion(version); setIsVersionModalOpen(true); };
  const handleCreateVersion = () => { setSelectedVersion(null); setIsVersionModalOpen(true); };

  const HISTORY_PAGE_SIZE = 10;

  const loadHistoryPage = async (versionId, page) => {
    try {
      const offset = page * HISTORY_PAGE_SIZE;
      const result = await ElementVersionHistory.filterWithTotal(
        { element_version_id: versionId }, '-created_at', HISTORY_PAGE_SIZE, offset
      );
      setVersionHistory(prev => ({ ...prev, [versionId]: result.data }));
      setHistoryPage(prev => ({ ...prev, [versionId]: { page, total: result.total } }));
    } catch (error) {
      console.error('Error loading version history:', error);
      setVersionHistory(prev => ({ ...prev, [versionId]: [] }));
      setHistoryPage(prev => ({ ...prev, [versionId]: { page: 0, total: 0 } }));
    }
  };

  const toggleVersionHistory = async (versionId) => {
    if (expandedVersions[versionId]) {
      setExpandedVersions(prev => ({ ...prev, [versionId]: false }));
      return;
    }
    if (!versionHistory[versionId]) await loadHistoryPage(versionId, 0);
    setExpandedVersions(prev => ({ ...prev, [versionId]: true }));
  };

  const formatFieldName = (field) => field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const handlePrimaryKeyChange = async (checked) => {
    try {
      await DataElement.update(elementId, { primary_key: checked });
      setElement(prev => ({ ...prev, primary_key: checked }));
    } catch (error) {
      console.error('Error updating primary key:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <Skeleton className="h-9 w-64 rounded-[5px]" />
        <Skeleton className="h-[120px] w-full rounded-[5px]" />
        <Skeleton className="h-[300px] w-full rounded-[5px]" />
      </div>
    );
  }

  if (!element) {
    return (
      <div className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col items-center justify-center gap-4">
        <p className="text-lg font-medium text-page-title">Element Not Found</p>
        <p className="text-sm text-page-muted">The requested data element could not be found.</p>
        <Link
          to={createPageUrl('data-elements')}
          className="flex items-center gap-1 w-fit text-sm no-underline text-nav-active hover:text-nav-active-hover transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Elements
        </Link>
      </div>
    );
  }

  const statusPill = currentVersion?.status ? STATUS_CONFIG[currentVersion.status.toUpperCase()] : null;
  const typePill = currentVersion?.data_type ? getDataTypePill(currentVersion.data_type) : null;

  return (
    <div className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Back link */}
      <Link
        to={createPageUrl('data-dictionary')}
        className="flex items-center gap-1 w-fit text-sm no-underline -mb-2 text-nav-active hover:text-nav-active-hover transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Dictionary
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] md:text-[32px] font-medium leading-tight text-page-title">{element.canonical_name}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            {statusPill && <Pill {...statusPill} />}
            {typePill && <Pill {...typePill} />}
            {currentVersion && (
              <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1.5 pr-2 bg-amber-100">
                <Crown size={10} color="rgba(180,130,0,0.9)" />
                <span className="text-xs font-medium leading-none text-badge-amber">
                  v{currentVersion.version_number}
                </span>
              </span>
            )}
            {element.is_public && (
              <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 bg-blue-100 text-xs font-medium text-blue-600">Public</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <button
            onClick={() => setIsDeleteDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-red-300 text-red-500 bg-card-bg text-sm font-medium cursor-pointer hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={20} strokeWidth={2.5} />
            Delete
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Pencil size={20} strokeWidth={2.5} />
            Edit
          </button>
          <button
            onClick={handleCreateVersion}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New Version
          </button>
        </div>
      </div>

      {/* Current Version Overview */}
      {currentVersion && (
        <div className="bg-card-bg rounded-[5px] px-5 py-4 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Crown size={16} color="rgba(180,130,0,0.9)" />
            <span className="text-base font-medium text-page-title">Current Version ({currentVersion.version_number})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 flex flex-col gap-1">
              <span className="text-xs font-medium text-page-muted uppercase tracking-wide">Definition</span>
              <p className="text-sm text-page-text">{currentVersion.definition || <span className="italic text-page-muted">No definition</span>}</p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-page-muted uppercase tracking-wide">Data Type</span>
                {typePill && <Pill {...typePill} />}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={element.primary_key || false}
                  onCheckedChange={handlePrimaryKeyChange}
                  className="data-[state=checked]:bg-[#377AD6] data-[state=checked]:border-[#377AD6]"
                />
                <div className="flex items-center gap-1">
                  <Key size={14} className="text-page-muted" />
                  <span className="text-sm text-page-text">Primary Key</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History */}
      <div className="bg-card-bg rounded-[5px] overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-[#377AD6]/10">
          <span className="text-base font-medium text-page-title">Version History</span>
          <FilterSelect value={statusFilter} onValueChange={setStatusFilter} placeholder="All Status">
            <FilterSelectItem value="all">All Status</FilterSelectItem>
            <FilterSelectItem value="APPROVED">Approved</FilterSelectItem>
            <FilterSelectItem value="PENDING_REVIEW">Pending Review</FilterSelectItem>
            <FilterSelectItem value="DRAFT">Draft</FilterSelectItem>
            <FilterSelectItem value="DEPRECATED">Deprecated</FilterSelectItem>
          </FilterSelect>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#377AD6]/10 hover:bg-transparent">
                <TableHead className="w-8"></TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Version</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Status</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Definition</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Data Type</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Changed By</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Created</TableHead>
                <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVersions.map((version) => {
                const vStatusPill = version.status ? STATUS_CONFIG[version.status.toUpperCase()] : null;
                const vTypePill = version.data_type ? getDataTypePill(version.data_type) : null;
                return (
                  <Fragment key={version.id}>
                    <TableRow className="border-b border-[#377AD6]/10 hover:bg-[#377AD6]/5 transition-colors">
                      <TableCell className="w-8 px-2">
                        <button
                          className="flex items-center justify-center size-6 rounded text-page-muted hover:text-[#377AD6] transition-colors cursor-pointer bg-transparent border-0"
                          onClick={() => toggleVersionHistory(version.id)}
                          title="View change history"
                        >
                          {expandedVersions[version.id]
                            ? <ChevronDown size={16} />
                            : <ChevronRight size={16} />
                          }
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-page-text">v{version.version_number}</span>
                          {version.id === currentVersion?.id && (
                            <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1.5 pr-2 bg-amber-100">
                              <Crown size={10} color="rgba(180,130,0,0.9)" />
                              <span className="text-xs font-medium leading-none text-badge-amber">Current</span>
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {vStatusPill && <Pill {...vStatusPill} />}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-page-muted max-w-xs truncate block">{version.definition}</span>
                      </TableCell>
                      <TableCell>
                        {vTypePill && <Pill {...vTypePill} />}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-page-muted">{version.changed_by || '—'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-page-muted">
                          {format(new Date(version.created_date), 'MMM d, yyyy')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {version.id !== currentVersion?.id && (
                            <button
                              onClick={() => handleSetAsCurrent(version)}
                              title="Set as current version"
                              className="flex items-center justify-center size-7 rounded text-amber-500 hover:bg-amber-50 transition-colors cursor-pointer bg-transparent border-0"
                            >
                              <Star size={15} />
                            </button>
                          )}
                          <button
                            onClick={() => handleEditVersion(version)}
                            className="flex items-center justify-center size-7 rounded text-page-muted hover:text-[#377AD6] hover:bg-[#377AD6]/5 transition-colors cursor-pointer bg-transparent border-0"
                          >
                            <Edit size={15} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedVersions[version.id] && (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0 border-b border-[#377AD6]/10">
                          <div className="bg-[#377AD6]/5 px-6 py-4">
                            <div className="flex items-center gap-2 mb-3">
                              <History size={14} className="text-page-muted" />
                              <span className="text-sm font-medium text-page-text">Change History</span>
                            </div>
                            {!versionHistory[version.id] ? (
                              <p className="text-sm text-page-muted">Loading...</p>
                            ) : versionHistory[version.id].length === 0 ? (
                              <p className="text-sm text-page-muted">No change history recorded</p>
                            ) : (
                              <div className="space-y-3">
                                {versionHistory[version.id].map((entry) => (
                                  <div key={entry.id} className="flex gap-3 text-sm">
                                    <div className="flex-shrink-0 w-36 text-page-muted text-xs">
                                      {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}
                                    </div>
                                    <div className="flex-shrink-0">
                                      <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 bg-[#377AD6]/10 text-xs font-medium text-[#377AD6]">
                                        {entry.action === 'INSERT' ? 'Created' : 'Updated'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0 text-page-muted text-xs">
                                      <User size={12} />
                                      {entry.changed_by || 'Unknown'}
                                    </div>
                                    <div className="flex-1">
                                      {entry.action === 'INSERT' ? (
                                        <span className="text-sm text-page-muted">{entry.change_description || 'Version created'}</span>
                                      ) : (
                                        <div className="space-y-1">
                                          {entry.change_description && (
                                            <p className="text-sm text-page-text italic">{entry.change_description}</p>
                                          )}
                                          {entry.changes && Object.keys(entry.changes).length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                              {Object.entries(entry.changes).map(([field, diff]) => (
                                                <span key={field} className="inline-flex items-center gap-1 bg-card-bg border border-[#377AD6]/20 rounded px-2 py-0.5 text-xs">
                                                  {field === 'processing_rule' && diff.action ? (
                                                    <>
                                                      <span className="font-medium text-page-text">Processing Rule</span>
                                                      <span className="text-page-muted">:</span>
                                                      <span className={diff.action === 'removed' ? 'text-red-500' : 'text-green-600'}>
                                                        {diff.action === 'added' && `+ ${diff.rule_name || 'Unknown'} (#${diff.execution_order})`}
                                                        {diff.action === 'removed' && `- ${diff.rule_name || 'Unknown'}`}
                                                        {diff.action === 'updated' && `~ ${diff.rule_name || 'Unknown'}`}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="font-medium text-page-text">{formatFieldName(field)}</span>
                                                      <span className="text-page-muted">:</span>
                                                      <span className="text-red-500 line-through max-w-[120px] truncate" title={diff.old}>
                                                        {diff.old || '(empty)'}
                                                      </span>
                                                      <span className="text-page-muted">&rarr;</span>
                                                      <span className="text-green-600 max-w-[120px] truncate" title={diff.new}>
                                                        {diff.new || '(empty)'}
                                                      </span>
                                                    </>
                                                  )}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {historyPage[version.id] && historyPage[version.id].total > HISTORY_PAGE_SIZE && (
                                  <div className="flex items-center justify-between pt-2 border-t border-[#377AD6]/10">
                                    <span className="text-xs text-page-muted">
                                      Showing {historyPage[version.id].page * HISTORY_PAGE_SIZE + 1}–{Math.min((historyPage[version.id].page + 1) * HISTORY_PAGE_SIZE, historyPage[version.id].total)} of {historyPage[version.id].total}
                                    </span>
                                    <div className="flex gap-1">
                                      <button
                                        className="h-7 px-3 text-xs rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                                        disabled={historyPage[version.id].page === 0}
                                        onClick={() => loadHistoryPage(version.id, historyPage[version.id].page - 1)}
                                      >
                                        Previous
                                      </button>
                                      <button
                                        className="h-7 px-3 text-xs rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                                        disabled={(historyPage[version.id].page + 1) * HISTORY_PAGE_SIZE >= historyPage[version.id].total}
                                        onClick={() => loadHistoryPage(version.id, historyPage[version.id].page + 1)}
                                      >
                                        Next
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Source Mappings */}
      <div className="bg-card-bg rounded-[5px] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-[#377AD6]/10">
          <Link2 size={16} color="#377AD6" />
          <span className="text-base font-medium text-page-title">Source Mappings</span>
          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 bg-[#377AD6]/10 text-xs font-medium text-[#377AD6] ml-1">{mappings.length}</span>
        </div>
        {mappings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Link2 size={36} className="text-page-muted opacity-30" />
            <p className="text-sm text-page-muted">No source mappings for this element</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#377AD6]/10 hover:bg-transparent">
                  <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Source</TableHead>
                  <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Source Field</TableHead>
                  <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Confidence</TableHead>
                  <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Transformation Notes</TableHead>
                  <TableHead className="text-xs font-medium text-page-muted uppercase tracking-wide">Last Validated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow
                    key={mapping.id}
                    className="border-b border-[#377AD6]/10 hover:bg-[#377AD6]/5 transition-colors cursor-pointer"
                    onClick={() => window.location.href = createPageUrl('source-mappings') + '/' + mapping.id}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#377AD6]">{mapping.source_name}</span>
                        {mapping.is_golden_source && (
                          <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1.5 pr-2 bg-amber-100">
                            <Star size={10} color="rgba(180,130,0,0.9)" />
                            <span className="text-xs font-medium leading-none text-badge-amber">Golden</span>
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-page-muted">{mapping.source_type}</span>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-[#377AD6]/8 text-[#377AD6] px-2 py-0.5 rounded-[3px]">{mapping.source_field_name}</code>
                    </TableCell>
                    <TableCell>
                      {mapping.mapping_confidence ? (() => {
                        const pct = (mapping.mapping_confidence * 100).toFixed(0);
                        const cfg = mapping.mapping_confidence >= 0.9
                          ? { bg: 'var(--pill-green-bg)', color: 'var(--pill-green-color)' }
                          : mapping.mapping_confidence >= 0.7
                          ? { bg: 'var(--pill-amber-bg)', color: 'var(--pill-amber-color)' }
                          : { bg: 'var(--pill-red-bg)', color: 'var(--pill-red-color)' };
                        return <Pill bg={cfg.bg} color={cfg.color} label={`${pct}%`} />;
                      })() : (
                        <span className="text-page-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-page-muted max-w-xs truncate block">{mapping.transformation_notes || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-page-muted">
                        {mapping.last_validated ? format(new Date(mapping.last_validated), 'MMM d, yyyy') : '—'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ElementVersionModal
        isOpen={isVersionModalOpen}
        onClose={handleVersionModalClose}
        version={selectedVersion}
        element={element}
        onVersionSaved={handleVersionSaved}
        initialTab={tabParam || "basic"}
      />

      <ElementEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        element={element}
        onElementUpdated={() => { setIsEditModalOpen(false); loadElementDetails(); }}
      />

      <DeleteDataElementDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={() => navigate(createPageUrl('data-elements'))}
        element={element}
      />
    </div>
  );
}
